import { createSlice, PayloadAction } from '@reduxjs/toolkit';
import { PURGE } from 'redux-persist';
import i18n from '../../i18n';
import { USER_DATA_EXPIRATION_MS } from '../../utils/constants';
import { AgeGroupValue } from '../../utils/constants';

// Helper function to generate visitor ID locally (Frontend-only)
const generateVisitorId = (): string => {
    const timestamp = Date.now();
    const randomPart = Math.random().toString(36).substr(2, 9);
    return `visitor_${timestamp}_${randomPart}`;
};

export interface UserState {
    isAuthenticated: boolean;
    visitorId: string;
    name: string;
    email: string;
    ageGroup: AgeGroupValue | '';
    nationality: string;
    selfieUrl: string;
    language: string;
    hasCompletedRegistration: boolean;
    privacyAccepted: boolean;
    message: string;
    // Frontend-only loading states
    isLoading: boolean;
    formError: string | null;
    // Timestamp tracking for automatic expiration
    createdAt: number | null;
    lastActivityAt: number | null;
    // Flag to indicate if data was expired and reset
    dataWasExpired: boolean;
}

// Helper function to get initial visitor ID from localStorage
const getInitialVisitorId = (): string => {
    if (typeof window !== 'undefined') {
        return localStorage.getItem('visitorId') || '';
    }
    return '';
};

const initialState: UserState = {
    isAuthenticated: false,
    visitorId: getInitialVisitorId(),
    name: '',
    email: '',
    ageGroup: '',
    nationality: '',
    selfieUrl: '',
    language: '',
    hasCompletedRegistration: false,
    privacyAccepted: false,
    message: '',
    isLoading: false,
    formError: null,
    createdAt: null,
    lastActivityAt: null,
    dataWasExpired: false,
};

export const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        setUserProfile: (state, action: PayloadAction<Partial<UserState>>) => {
            const now = Date.now();
            return { 
                ...state, 
                ...action.payload,
                // Set createdAt if this is the first time setting user data
                createdAt: state.createdAt || now,
                lastActivityAt: now,
                formError: null // Clear any previous form errors
            };
        },
        setAuthenticated: (state, action: PayloadAction<boolean>) => {
            state.isAuthenticated = action.payload;
            state.lastActivityAt = Date.now();
        },
        setSelfie: (state, action: PayloadAction<string>) => {
            state.selfieUrl = action.payload;
            state.lastActivityAt = Date.now();
            
            // Store selfie in localStorage for persistence
            localStorage.setItem('selfie', action.payload);
            console.log('📸 Selfie stored in Redux and localStorage');
        },
        setLanguage: (state, action: PayloadAction<string>) => {
            const now = Date.now();
            state.language = action.payload;
            state.createdAt = state.createdAt || now;
            state.lastActivityAt = now;
            
            // Change i18n language when language is set in Redux
            i18n.changeLanguage(action.payload);
            
            // Explicitly set HTML lang attribute as fallback
            document.documentElement.setAttribute('lang', action.payload);
        },
        // Frontend-only registration (replaces signupVisitor API call)
        registerVisitor: (state, action: PayloadAction<{
            name: string;
            email: string;
            ageGroup: AgeGroupValue;
            nationality: string;
        }>) => {
            const now = Date.now();
            const visitorId = generateVisitorId();
            
            state.visitorId = visitorId;
            state.name = action.payload.name;
            state.email = action.payload.email;
            state.ageGroup = action.payload.ageGroup;
            state.nationality = action.payload.nationality;
            state.hasCompletedRegistration = true;
            state.isAuthenticated = true;
            state.message = 'Registration completed successfully!';
            state.formError = null;
            state.createdAt = now;
            state.lastActivityAt = now;
            
            // Store visitor data in localStorage for persistence
            localStorage.setItem('visitorId', visitorId);
            localStorage.setItem('visitorCreatedAt', now.toString());
            
            console.log('✅ Frontend-only registration completed:', {
                visitorId,
                name: action.payload.name,
                email: action.payload.email,
                ageGroup: action.payload.ageGroup,
                nationality: action.payload.nationality
            });
        },
        // Frontend-only registration validation
        setRegistrationLoading: (state, action: PayloadAction<boolean>) => {
            state.isLoading = action.payload;
            if (action.payload) {
                state.formError = null;
            }
        },
        // Frontend-only form error handling
        setFormError: (state, action: PayloadAction<string>) => {
            state.formError = action.payload;
            state.isLoading = false;
        },
        // Complete registration process
        completeRegistration: (state) => {
            const now = Date.now();
            state.hasCompletedRegistration = true;
            state.isAuthenticated = true;
            state.createdAt = state.createdAt || now;
            state.lastActivityAt = now;
            state.formError = null;
            
            console.log('✅ Registration marked as complete');
        },
        // Clear form error
        clearFormError: (state) => {
            state.formError = null;
        },
        // Set visitor ID (useful for restoring from localStorage)
        setVisitorId: (state, action: PayloadAction<string>) => {
            state.visitorId = action.payload;
            if (action.payload) {
                localStorage.setItem('visitorId', action.payload);
            }
            state.lastActivityAt = Date.now();
        },
        // Update last activity timestamp
        updateActivity: (state) => {
            state.lastActivityAt = Date.now();
        },
        // Clear the dataWasExpired flag after handling
        clearDataExpiredFlag: (state) => {
            state.dataWasExpired = false;
        },
        // Check if user data has expired and reset if needed
        checkExpiration: (state) => {
            const now = Date.now();
            
            if (state.lastActivityAt && (now - state.lastActivityAt) > USER_DATA_EXPIRATION_MS) {
                // Data has expired, reset to initial state but preserve visitorId and language
                const preservedVisitorId = state.visitorId;
                const preservedLanguage = state.language;
                
                // Reset to initial state
                Object.assign(state, initialState);
                
                // Restore preserved data
                if (preservedVisitorId) {
                    state.visitorId = preservedVisitorId;
                }
                if (preservedLanguage) {
                    state.language = preservedLanguage;
                }
                
                // Set a flag to indicate data was expired (will be used for navigation)
                state.dataWasExpired = true;
                
                return;
            }
            
            // Update activity if not expired
            state.lastActivityAt = now;
        },
        logout: (state) => {
            // Clear all user data except language preference
            const language = state.language;
            Object.assign(state, { ...initialState, language });
            
            // Clear localStorage data
            localStorage.removeItem('selfie');
            localStorage.removeItem('visitorId');
            localStorage.removeItem('visitorCreatedAt');
            
            console.log('🚪 User logged out - data cleared');
        },
        // Action to completely reset user state (for testing or admin purposes)
        resetUserState: () => {
            // Clear localStorage data
            localStorage.removeItem('selfie');
            localStorage.removeItem('visitorId');
            localStorage.removeItem('visitorCreatedAt');
            
            console.log('🔄 User state completely reset');
            return initialState;
        },
        // Frontend-only selfie processing (replaces uploadSelfie API call)
        processSelfie: (state, action: PayloadAction<{
            selfieDataUrl: string;
            filename?: string;
        }>) => {
            const { selfieDataUrl, filename = 'selfie.jpg' } = action.payload;
            
            state.selfieUrl = selfieDataUrl;
            state.message = 'Selfie captured successfully!';
            state.formError = null;
            state.lastActivityAt = Date.now();
            
            // Store selfie in localStorage for persistence with error handling
            try {
                localStorage.setItem('selfie', selfieDataUrl);
                console.log('📸 Frontend-only selfie processed and stored:', {
                    visitorId: state.visitorId,
                    filename,
                    dataSize: `${Math.round(selfieDataUrl.length / 1024)}KB`,
                    storedInLocalStorage: true
                });
            } catch (error) {
                console.error('❌ Failed to store selfie in localStorage:', error);
                console.warn('⚠️ Selfie will only be available in current session');
                
                // Still process the selfie in Redux, just without localStorage persistence
                console.log('📸 Frontend-only selfie processed (session only):', {
                    visitorId: state.visitorId,
                    filename,
                    dataSize: `${Math.round(selfieDataUrl.length / 1024)}KB`,
                    storedInLocalStorage: false,
                    reason: 'localStorage storage failed'
                });
            }
        },
        // Restore user data from localStorage (replaces fetchVisitorData API call)
        restoreUserData: (state) => {
            const storedVisitorId = localStorage.getItem('visitorId');
            const storedSelfie = localStorage.getItem('selfie');
            const storedCreatedAt = localStorage.getItem('visitorCreatedAt');
            
            if (storedVisitorId) {
                state.visitorId = storedVisitorId;
                console.log('🔄 Restored visitor ID from localStorage:', storedVisitorId);
            }
            
            if (storedSelfie && !state.selfieUrl) {
                state.selfieUrl = storedSelfie;
                console.log('📸 Restored selfie from localStorage');
            }
            
            if (storedCreatedAt) {
                const createdAt = parseInt(storedCreatedAt);
                state.createdAt = createdAt;
                state.lastActivityAt = Date.now();
                console.log('⏰ Restored creation timestamp from localStorage');
            }
            
            state.formError = null;
        },
    },
    extraReducers: (builder) => {
        // Handle the PURGE action from redux-persist
        builder.addCase(PURGE, () => {
            return initialState;
        });
    },
});

export const { 
    setUserProfile, 
    setAuthenticated, 
    setSelfie, 
    setLanguage,
    registerVisitor,
    setRegistrationLoading,
    setFormError,
    completeRegistration,
    clearFormError,
    setVisitorId,
    updateActivity,
    clearDataExpiredFlag,
    checkExpiration,
    logout,
    resetUserState,
    processSelfie,
    restoreUserData
} = userSlice.actions;

export default userSlice.reducer; 